import { useCallback, useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { useLocale, useStrings } from '../../state/useLocale';
import { usePhoneLayout } from '../../lib/phone-layout';

/**
 * The workstation frame.
 *
 * Two columns: the ticket on the left, the work in the middle. There is no
 * third column. Help used to live in one permanently, which meant a wall of
 * greyed-out hint rungs was on screen while the learner was still thinking;
 * it now appears in the left rail only once it has been asked for.
 *
 * The reflog is a disclosure rather than a fixed pane. It is the whole point
 * of one level and noise on the other nineteen, so it opens itself when the
 * level is about it and stays shut otherwise.
 *
 * Sizes are written as CSS custom properties on the frame, so the grid in
 * play.css picks them up with no stylesheet change, and nothing is written
 * until the learner actually drags something.
 *
 * Mounting the frame is what starts the workstation's arrival: play.css
 * fades the HUD, the rail, the graph and the shell in, in that order, and
 * nothing here waits for it. The terminal is mounted and fitted on the first
 * frame exactly as it would be without the sequence; only its ancestors'
 * opacity moves. Play keys this component on the level, which is why every
 * level opens this way and a reset of the same level does not.
 */

export interface PlayFrameProps {
  hud: ReactNode;
  /** The whole left rail: ticket, objectives, help. */
  brief: ReactNode;
  graph: ReactNode;
  /** Commit detail strip, inside the graph pane. */
  detail: ReactNode;
  reflog: ReactNode;
  /** Open the reflog on arrival, for levels whose subject it is. */
  reflogDefaultOpen?: boolean;
  /** No repository yet: shrink the graph and drop the reflog entirely. */
  bare?: boolean;
  /** A level that never touches git, with no repository: no graph pane at all (lib/uses-git.ts). */
  noGraph?: boolean;
  /** The entire bottom pane including its own section and head. */
  work: ReactNode;
  /** Open Iris, from the phone layout's tab bar. Absent where the level has no Iris. */
  onIris?(): void;
}

/**
 * The panel a phone shows. One at a time, because each of them needs the
 * whole of a phone's height to be usable, and the terminal most of all: the
 * stacked layout this replaced put it 2,200px down a 375px-wide page, 274px
 * tall, under the keyboard (measured 2026-10-10). Everything stays mounted;
 * only what is shown changes, so the shell and the scrollback live on.
 */
type PhonePanel = 'task' | 'work' | 'graph';

type Axis = 'rail-left' | 'term-h';

const BOUNDS: Record<Axis, { min: number; max: number }> = {
  'rail-left': { min: 260, max: 620 },
  'term-h': { min: 160, max: 620 },
};

const SELECTOR: Record<Axis, string> = {
  'rail-left': '.rail-brief',
  'term-h': '.pane-term',
};

const KEY = 'engineeros.layout.v1';
const REFLOG_KEY = 'engineeros.reflog.open';

type Stored = Partial<Record<Axis, number>>;

const clamp = (axis: Axis, v: number) => Math.min(BOUNDS[axis].max, Math.max(BOUNDS[axis].min, Math.round(v)));

function load(): Stored {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Stored;
    const out: Stored = {};
    for (const axis of Object.keys(BOUNDS) as Axis[]) {
      const v = parsed[axis];
      if (typeof v === 'number' && Number.isFinite(v)) out[axis] = clamp(axis, v);
    }
    return out;
  } catch {
    return {};
  }
}

function save(next: Stored): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    /* private mode, or storage disabled */
  }
}

/**
 * Current size of one axis, in pixels.
 *
 * Reading the custom property works only once the learner has dragged: an
 * unregistered custom property comes back from `getComputedStyle` as its
 * literal token string, and `--term-h` defaults to a `clamp()` expression,
 * which parses to NaN. Measuring the box is right in both cases.
 */
function current(scope: HTMLElement, axis: Axis): number {
  const raw = Number.parseFloat(getComputedStyle(scope).getPropertyValue(`--${axis}`).trim());
  if (Number.isFinite(raw)) return raw;
  const el = scope.querySelector(SELECTOR[axis]) ?? scope.closest('.play')?.querySelector(SELECTOR[axis]);
  if (el) {
    const box = el.getBoundingClientRect();
    return axis === 'term-h' ? box.height : box.width;
  }
  return BOUNDS[axis].min;
}

export function PlayFrame({
  hud,
  brief,
  graph,
  detail,
  reflog,
  reflogDefaultOpen = false,
  bare = false,
  noGraph = false,
  work,
  onIris,
}: PlayFrameProps) {
  const s = useStrings().play;
  const rtl = useLocale().dir === 'rtl';
  const frameRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLElement>(null);
  const [stored, setStored] = useState<Stored>(load);
  const phone = usePhoneLayout();
  const [panel, setPanel] = useState<PhonePanel>('task');

  // The level's subject decides the first impression; after that the
  // learner's own choice wins, on this browser.
  const [reflogOpen, setReflogOpen] = useState(() => {
    try {
      const saved = localStorage.getItem(REFLOG_KEY);
      if (saved !== null) return saved === '1';
    } catch {
      /* storage disabled */
    }
    return reflogDefaultOpen;
  });

  const toggleReflog = useCallback(() => {
    setReflogOpen((open) => {
      const next = !open;
      try {
        localStorage.setItem(REFLOG_KEY, next ? '1' : '0');
      } catch {
        /* storage disabled */
      }
      return next;
    });
  }, []);

  const hostFor = useCallback((axis: Axis) => (axis === 'term-h' ? stageRef.current : frameRef.current), []);

  const commit = useCallback((axis: Axis, value: number) => {
    setStored((prev) => {
      const next = { ...prev, [axis]: clamp(axis, value) };
      save(next);
      return next;
    });
    window.dispatchEvent(new Event('resize'));
  }, []);

  const reset = useCallback(
    (axis: Axis) => {
      hostFor(axis)?.style.removeProperty(`--${axis}`);
      setStored((prev) => {
        const next = { ...prev };
        delete next[axis];
        save(next);
        return next;
      });
      window.dispatchEvent(new Event('resize'));
    },
    [hostFor],
  );

  const onPointerDown = useCallback(
    (axis: Axis, invert: boolean) => (e: React.PointerEvent<HTMLDivElement>) => {
      const handle = e.currentTarget;
      const host = hostFor(axis);
      if (!host) return;
      e.preventDefault();
      handle.setPointerCapture(e.pointerId);
      handle.classList.add('is-dragging');

      const vertical = axis === 'term-h';
      const startPos = vertical ? e.clientY : e.clientX;
      const startVal = current(host, axis);
      const prevCursor = document.body.style.cursor;
      document.body.style.cursor = vertical ? 'row-resize' : 'col-resize';
      document.body.style.userSelect = 'none';

      const move = (ev: PointerEvent) => {
        const delta = (vertical ? ev.clientY : ev.clientX) - startPos;
        // Written straight to the DOM: re-rendering React at pointer rate
        // would re-render the terminal and the graph on every frame.
        host.style.setProperty(`--${axis}`, `${clamp(axis, startVal + (invert ? -delta : delta))}px`);
      };

      const up = () => {
        handle.classList.remove('is-dragging');
        document.body.style.cursor = prevCursor;
        document.body.style.userSelect = '';
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup', up);
        window.removeEventListener('pointercancel', up);
        commit(axis, current(host, axis));
      };

      window.addEventListener('pointermove', move);
      window.addEventListener('pointerup', up);
      window.addEventListener('pointercancel', up);
    },
    [commit, hostFor],
  );

  const onKeyDown = useCallback(
    (axis: Axis, invert: boolean) => (e: React.KeyboardEvent<HTMLDivElement>) => {
      const host = hostFor(axis);
      if (!host) return;
      const vertical = axis === 'term-h';
      const back = vertical ? 'ArrowUp' : 'ArrowLeft';
      const fwd = vertical ? 'ArrowDown' : 'ArrowRight';
      if (e.key === 'Home') {
        e.preventDefault();
        reset(axis);
        return;
      }
      if (e.key !== back && e.key !== fwd) return;
      e.preventDefault();
      commit(axis, current(host, axis) + (e.key === fwd ? 16 : -16) * (invert ? -1 : 1));
    },
    [commit, hostFor, reset],
  );

  // Restored sizes are applied inline so a stored layout survives a reload
  // without a flash of the default.
  const frameStyle: CSSProperties = {};
  if (stored['rail-left'] !== undefined) frameStyle['--rail-left' as never] = `${stored['rail-left']}px` as never;
  const stageStyle: CSSProperties = {};
  if (stored['term-h'] !== undefined) stageStyle['--term-h' as never] = `${stored['term-h']}px` as never;

  /**
   * `invert` is written for a left-to-right page: the ticket rail is on the
   * left, so dragging right grows it. Mirror the page and the rail is on the
   * right, the same drag has to shrink it, and `ev.clientX - startPos` has the
   * wrong sign — so a horizontal axis flips its inversion in RTL. This is also
   * what swaps ArrowLeft and ArrowRight, since both go through the same flag.
   * A vertical drag has no direction to mirror and is left alone.
   */
  const handle = (axis: Axis, invert: boolean, extra: string, label: string) => {
    const flip = axis === 'term-h' ? invert : invert !== rtl;
    return (
      <div
        className={`rsz ${axis === 'term-h' ? 'rsz-y' : 'rsz-x'} ${extra}`}
        role="separator"
        aria-orientation={axis === 'term-h' ? 'horizontal' : 'vertical'}
        aria-label={label}
        aria-valuemin={BOUNDS[axis].min}
        aria-valuemax={BOUNDS[axis].max}
        aria-valuenow={stored[axis]}
        tabIndex={0}
        onPointerDown={onPointerDown(axis, flip)}
        onDoubleClick={() => reset(axis)}
        onKeyDown={onKeyDown(axis, flip)}
      />
    );
  };

  return (
    <div className="play" ref={frameRef} style={frameStyle} data-panel={phone ? panel : undefined}>
      <header className="hud">{hud}</header>

      <aside className="rail rail-brief">{brief}</aside>

      <main className={`stage${bare ? ' is-bare' : ''}${noGraph ? ' no-graph' : ''}`} ref={stageRef} style={stageStyle}>
        {!noGraph && (
          <section className="pane pane-graph">
            <div className="pane-head">
              <h2 className="label">{s.frame.graph}</h2>
            </div>
            <div className="pane-body scroll">{graph}</div>
            {detail}
          </section>
        )}

        {!bare && (
          <section className={`pane pane-reflog${reflogOpen ? '' : ' is-shut'}`}>
            <div className="pane-head">
              <button className="disclose" type="button" onClick={toggleReflog} aria-expanded={reflogOpen}>
                <span className="disclose-mark" aria-hidden="true" />
                <span className="label">{s.frame.reflog}</span>
              </button>
            </div>
            {reflogOpen && <div className="pane-body scroll">{reflog}</div>}
          </section>
        )}

        {!bare && handle('term-h', true, 'rsz-row', s.frame.resizeTerminal)}
        {work}
      </main>

      {handle('rail-left', false, 'rsz-left', s.frame.resizeTicket)}

      {phone && (
        <nav className="phone-tabs" aria-label={s.phone.tabs}>
          {(noGraph ? (['task', 'work'] as const) : (['task', 'work', 'graph'] as const)).map((id) => (
            <button
              key={id}
              type="button"
              className={`phone-tab${panel === id ? ' is-on' : ''}`}
              aria-pressed={panel === id}
              onClick={() => setPanel(id)}
            >
              {id === 'task' ? s.phone.task : id === 'work' ? s.phone.terminal : s.phone.graph}
            </button>
          ))}
          {onIris && (
            <button type="button" className="phone-tab phone-tab-iris" onClick={onIris}>
              {s.phone.iris}
            </button>
          )}
        </nav>
      )}
    </div>
  );
}

export default PlayFrame;
